import { Controller } from "@hotwired/stimulus"
import { visibleItems, itemFromEvent, isFolder, submitAsStream, controllerFor, toast } from "./cloud_helpers"

// Selezione (clic, Ctrl/Cmd, Shift, checkbox), tastiera, rinomina inline,
// barra di massa. Parla con cloud-inspector e cloud-context-menu.
export default class extends Controller {
  static targets = ["checkbox", "toolbar", "count", "name", "area"]
  static values = { moveUrl: String, destroyUrl: String, newMoveUrl: String }

  connect() {
    this._selected = new Set()
    this._anchor = null
    this._focused = null
    this._renaming = null
    this.coarse = window.matchMedia("(pointer: coarse)").matches
    // I menu ⋯ vengono spostati nel body dal controller popup, quindi le loro
    // voci non raggiungono questo controller: le ascoltiamo sul documento.
    this._onDocumentClick = (event) => {
      const button = event.target.closest("[data-cloud-action]")
      if (!button) return
      const item = this.element.querySelector(`[data-cloud-item][data-item-id="${button.dataset.cloudItemId}"]`)
      if (!item) return
      event.preventDefault()
      document.body.click()
      if (button.dataset.cloudAction === "rename") this.rename(item)
    }
    document.addEventListener("click", this._onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick)
  }

  // ── Selezione ─────────────────────────────────────────────────────────────

  click(event) {
    const item = itemFromEvent(event)
    if (!item || this._renaming) return
    if (event.target.closest("a, button, input, label, [data-controller='popup']")) {
      // Il nome della cartella è un link: sul desktop un clic secco lo segue.
      if (event.target.closest("a") && !(event.metaKey || event.ctrlKey || event.shiftKey)) return
      event.preventDefault()
    }
    if (event.detail > 1) return
    if (event.shiftKey) {
      this.selectRange(item)
    } else if (event.metaKey || event.ctrlKey) {
      this.toggleItem(item)
    } else if (this.coarse) {
      // Touch: un tocco apre la cartella o l'anteprima del file.
      isFolder(item) ? window.Turbo.visit(item.dataset.openUrl) : this.peek(item)
      return
    } else {
      this.selectOnly(item)
    }
    this.focusItem(item)
    if (this.inspector?.isOpen && !isFolder(item) && this._selected.size === 1) this.inspector.open(item)
  }

  open(event) {
    const item = itemFromEvent(event)
    if (!item || this._renaming) return
    if (event.target.closest("[data-controller='popup'], input, label")) return
    event.preventDefault()
    this.activate(item)
  }

  activate(item) {
    isFolder(item) ? window.Turbo.visit(item.dataset.openUrl) : this.peek(item)
  }

  peek(item) {
    if (!item || !item.dataset.peekUrl) return
    this.selectOnly(item)
    this.inspector?.open(item)
  }

  toggle(event) {
    const item = itemFromEvent(event)
    if (!item) return
    event.target.checked ? this._selected.add(item.dataset.itemId) : this._selected.delete(item.dataset.itemId)
    this._anchor = item
    this.render()
  }

  selectOnly(item) {
    this._selected = new Set([item.dataset.itemId])
    this._anchor = item
    this.render()
  }

  toggleItem(item) {
    const id = item.dataset.itemId
    this._selected.has(id) ? this._selected.delete(id) : this._selected.add(id)
    this._anchor = item
    this.render()
  }

  selectRange(item) {
    const items = this.items()
    const from = this._anchor ? items.indexOf(this._anchor) : -1
    const to = items.indexOf(item)
    if (from === -1 || to === -1) return this.selectOnly(item)
    const [a, b] = from < to ? [from, to] : [to, from]
    items.slice(a, b + 1).forEach((el) => this._selected.add(el.dataset.itemId))
    this.render()
  }

  selectAll() {
    this.items().forEach((el) => this._selected.add(el.dataset.itemId))
    this.render()
  }

  clear() {
    this._selected.clear()
    this.render()
  }

  isSelected(item) { return this._selected.has(item.dataset.itemId) }
  get selectedIds() { return Array.from(this._selected) }
  get selectedItems() { return this.items().filter((el) => this.isSelected(el)) }

  // Applica lo stato a tessere, righe e checkbox (griglia e lista in parallelo).
  render() {
    this.element.querySelectorAll("[data-cloud-item]").forEach((el) => {
      const on = this._selected.has(el.dataset.itemId)
      el.classList.toggle("is-selected", on)
      el.setAttribute("aria-selected", on)
      const box = el.querySelector("input[type=checkbox]")
      if (box) box.checked = on
    })
    const count = this._selected.size
    if (this.hasToolbarTarget) this.toolbarTarget.hidden = count === 0
    if (this.hasCountTarget) this.countTarget.textContent = count
    this.dispatch("selection", { detail: { ids: this.selectedIds } })
  }

  // ── Fuoco e tastiera ──────────────────────────────────────────────────────

  items() { return visibleItems(this.element) }

  focusItem(item) {
    if (this._focused && this._focused !== item) this._focused.setAttribute("tabindex", "-1")
    this._focused = item
    item.setAttribute("tabindex", "0")
    item.focus({ preventScroll: false })
  }

  keydown(event) {
    if (event.target.matches("input, textarea, select, [contenteditable]")) return
    if (this._renaming) return
    const key = event.key
    const mod = event.metaKey || event.ctrlKey

    if (key === "Escape") {
      if (this.contextMenu?.isOpen) return this.contextMenu.close()
      if (this.inspector?.isOpen) return this.inspector.close()
      if (this._selected.size) { event.preventDefault(); this.clear() }
      return
    }
    if (mod && key.toLowerCase() === "a") { event.preventDefault(); return this.selectAll() }
    if (event.altKey || mod) return

    const current = this._focused && this.items().includes(this._focused) ? this._focused : null
    switch (key) {
      case "ArrowRight": case "ArrowLeft": case "ArrowDown": case "ArrowUp":
        event.preventDefault()
        return this.moveFocus(key, event.shiftKey)
      case "Enter":
        if (current) { event.preventDefault(); this.activate(current) }
        return
      case " ":
        if (current) {
          event.preventDefault()
          if (this.inspector?.isOpen && this.inspector.currentId === current.dataset.itemId) this.inspector.close()
          else this.peek(current)
        }
        return
      case "F2":
        if (current) { event.preventDefault(); this.rename(current) }
        return
      case "Delete": case "Backspace":
        if (this._selected.size) { event.preventDefault(); this.deleteSelection() }
        return
      case "Home": case "End": {
        const items = this.items()
        if (!items.length) return
        event.preventDefault()
        const target = key === "Home" ? items[0] : items[items.length - 1]
        event.shiftKey ? this.selectRange(target) : this.selectOnly(target)
        return this.focusItem(target)
      }
    }
  }

  moveFocus(key, extend) {
    const items = this.items()
    if (!items.length) return
    const current = this._focused && items.includes(this._focused) ? this._focused : null
    if (!current) {
      const first = items[0]
      this.selectOnly(first)
      return this.focusItem(first)
    }
    const index = items.indexOf(current)
    const columns = this.columnsOf(items, current)
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[key]
    let next = index + step
    if (next < 0 || next >= items.length) {
      if (key === "ArrowDown" || key === "ArrowUp") return
      next = Math.max(0, Math.min(items.length - 1, next))
    }
    const target = items[next]
    extend ? this.selectRange(target) : this.selectOnly(target)
    this.focusItem(target)
    target.scrollIntoView({ block: "nearest" })
    if (this.inspector?.isOpen && !isFolder(target)) this.inspector.open(target)
  }

  // Quante tessere stanno su una riga della sezione dell'elemento corrente.
  columnsOf(items, current) {
    const top = current.offsetTop
    const container = current.parentElement
    const row = items.filter((el) => el.parentElement === container && Math.abs(el.offsetTop - top) < 4)
    return Math.max(1, row.length)
  }

  // ── Rinomina inline ───────────────────────────────────────────────────────

  renameFromMenu(event) {
    const item = itemFromEvent(event) || this.selectedItems[0]
    document.body.click() // chiude popup e menu contestuale
    if (item) this.rename(item)
  }

  rename(item) {
    const name = item.querySelector("[data-cloud-select-target='name']")
    if (!name || this._renaming) return
    this._renaming = item
    const input = document.createElement("input")
    input.type = "text"
    input.className = "cloud-item__rename"
    input.value = item.dataset.label
    input.setAttribute("aria-label", name.textContent)
    name.replaceWith(input)
    input.focus()
    const dot = input.value.lastIndexOf(".")
    input.setSelectionRange(0, dot > 0 && !isFolder(item) ? dot : input.value.length)

    const finish = async (commit) => {
      if (!this._renaming) return
      this._renaming = null
      const value = input.value.trim()
      if (commit && value && value !== item.dataset.label) {
        const body = new FormData()
        body.append(item.dataset.renameParam, value)
        body.append("_method", "patch")
        const response = await submitAsStream(item.dataset.renameUrl, { body })
        if (!response.ok) toast(input.getAttribute("data-error") || "", { kind: "error" })
        this.element.dispatchEvent(new CustomEvent("cloud:changed", { bubbles: true }))
      } else {
        input.replaceWith(name)
      }
    }
    input.addEventListener("keydown", (e) => {
      e.stopPropagation()
      if (e.key === "Enter") { e.preventDefault(); finish(true) }
      if (e.key === "Escape") { e.preventDefault(); finish(false) }
    })
    input.addEventListener("blur", () => finish(true))
    input.addEventListener("click", (e) => e.stopPropagation())
    input.addEventListener("dblclick", (e) => e.stopPropagation())
  }

  // ── Azioni ────────────────────────────────────────────────────────────────

  openFromMenu(event) {
    const item = itemFromEvent(event) || this.selectedItems[0]
    if (item) window.Turbo.visit(item.dataset.openUrl)
  }

  peekFromMenu() {
    const item = this.selectedItems.find((el) => el.dataset.peekUrl)
    if (item) this.peek(item)
  }

  moveSelection() {
    const ids = this.selectedIds
    if (!ids.length) return
    const url = new URL(this.newMoveUrlValue, window.location.origin)
    ids.forEach((id) => url.searchParams.append("ids[]", id))
    const drawer = controllerFor(document.body, "drawer")
    const frame = document.getElementById("drawer_frame")
    if (!drawer || !frame) return window.Turbo.visit(url.toString())
    drawer.open()
    frame.src = url.toString()
  }

  async deleteSelection() {
    const items = this.selectedItems
    if (!items.length) return
    const message = items.length === 1
      ? items[0].dataset.deleteConfirm
      : this.element.dataset.cloudSelectConfirmMany.replace("%{count}", items.length)
    if (!window.confirm(message)) return
    const body = new FormData()
    items.forEach((el) => body.append("ids[]", el.dataset.itemId))
    body.append("folder_id", this.element.dataset.cloudUploadFolderIdValue || "")
    const hadFolders = items.some(isFolder)
    const response = await submitAsStream(this.destroyUrlValue, { method: "DELETE", body })
    if (!response.ok) return toast(this.element.dataset.cloudSelectDeleteFailed, { kind: "error" })
    this.clear()
    if (this.inspector?.isOpen) this.inspector.close()
    if (hadFolders) window.Turbo.visit(window.location.href, { action: "replace" })
    else this.element.dispatchEvent(new CustomEvent("cloud:changed", { bubbles: true }))
  }

  stop(event) { event.stopPropagation() }

  get inspector() { return controllerFor(this.element, "cloud-inspector") }
  get contextMenu() { return controllerFor(this.element, "cloud-context-menu") }
}
